import type { SceneState } from './territory-paint';

/** The still part of the territory — ground and grid — painted once into an offscreen layer
 *  and copied at every frame (B-18): it changes only with the size of the page or the theme. */

const GRID_FINE = 24;
const GRID_BOLD = 120;

type BackgroundScene = Pick<SceneState, 'palette' | 'size' | 'node'>;

type Point = readonly [number, number];

/** One stroke per line, as before the cache: crossings keep the same, slightly darker, dots. */
function stroke(cx: CanvasRenderingContext2D, [x1, y1]: Point, [x2, y2]: Point) {
  cx.beginPath();
  cx.moveTo(x1, y1);
  cx.lineTo(x2, y2);
  cx.stroke();
}

function grid(cx: CanvasRenderingContext2D, { palette, size, node }: BackgroundScene): void {
  cx.lineWidth = 1;
  for (const [step, colour, alpha] of [
    [GRID_FINE, palette.line, 0.55],
    [GRID_BOLD, palette.line2, 0.35],
  ] as const) {
    cx.strokeStyle = colour;
    cx.globalAlpha = alpha;
    for (let x = node.x % step; x < size.w; x += step) stroke(cx, [x, 0], [x, size.h]);
    for (let y = node.y % step; y < size.h; y += step) stroke(cx, [0, y], [size.w, y]);
  }
  cx.globalAlpha = 1;
}

/** Repaints the layer for the current size, device pixel ratio and palette. */
export function paintBackground(
  layer: HTMLCanvasElement,
  scene: BackgroundScene,
  dpr: number,
): void {
  layer.width = scene.size.w * dpr;
  layer.height = scene.size.h * dpr;
  const cx = layer.getContext('2d');
  if (cx === null) return;
  cx.setTransform(dpr, 0, 0, dpr, 0, 0);
  cx.fillStyle = scene.palette.map;
  cx.fillRect(0, 0, scene.size.w, scene.size.h);
  grid(cx, scene);
}
